import { ToastError, ToastSuccess, ToastInfo } from "components/Global/Toasts/Toasty";
import { call, put, takeLatest } from "redux-saga/effects";
import { get, omit, sortBy } from "lodash";
import { defaultReturn, generateDefaultActions, makePayload, makeWritePayload, requestSaga } from "shared-utility/store/utils/helpers";

import uploadFile from "../../../../helpers/sourceFileUploadHelper";

const initialState = {
  currentUserId: null,
  isLoading: false,
  isUploading: false,
  selectedDealId: null,
  selectedFile: null,
  selectedPropertyId: null,
  selectedSourceFileVersions: null,
  sourceFileDetails: null,
  allVersionsForDeal: null,
  sourceFileTypes: [],
  sourceId: null,
  typeId: null,
  uploadErrorMessage: null,
  uploadSuccessful: false,
  isPortfolioLevel: false,
  largeMriFileModal: false
};

// ACTIONS
export const GET_SOURCE_FILE_VERSIONS = generateDefaultActions("GET_SOURCE_FILE_VERSIONS");
export const getSourceFileVersions = payload => makePayload(GET_SOURCE_FILE_VERSIONS.value, payload);
function* getSourceFileVersionsSaga({ dealId, selectedSourceFile }) {
  const url = `/source-files/versions/${dealId}/${selectedSourceFile.id}`;
  yield requestSaga(GET_SOURCE_FILE_VERSIONS, url, "GET", null, null, null, true);
}

export const GET_ALL_VERSIONS = generateDefaultActions("GET_ALL_VERSIONS");
export const getAllVersions = payload => makePayload(GET_ALL_VERSIONS.value, payload);
function* getAllVersionsSaga({ dealId }) {
  const url = `/source-files/deal/${dealId}/versions`;
  yield requestSaga(GET_ALL_VERSIONS, url, "GET", null, null, null, true);
}

export const UPLOAD_SOURCE_FILE = generateDefaultActions("UPLOAD_SOURCE_FILE");
export const uploadSourceFile = (dealId, propertyId, userId, sourceId, typeId, file, isPortfolioLevel, companyId) => makePayload(UPLOAD_SOURCE_FILE.value, { dealId, propertyId, userId, sourceId, typeId, file, isPortfolioLevel, companyId });
function* uploadSourceFileSaga(payload) {
  try {
    const url = (payload.typeId === "rent-roll" || payload.typeId === "mf-rent-roll" || payload.typeId === "mf-rent-roll-charge-code") ? "/rent-rolls/upload" : "/source-files";
    const response = yield call(uploadFile, { ...payload, url }, "POST");
    yield put({ type: UPLOAD_SOURCE_FILE.success, payload: response });
  } catch (error) {
    const userFriendlyError = get(error, "response.data.error", "Upload encountered error. Please choose a different file and try again.");
    yield put({ type: UPLOAD_SOURCE_FILE.failure, payload: { userFriendlyError, error } });
  }
}

export const GET_SOURCE_FILE_TYPES = generateDefaultActions("GET_SOURCE_FILE_TYPES");
export const getSourceFileTypes = payload => makePayload(GET_SOURCE_FILE_TYPES.value, payload);
function* getSourceFileTypesSaga() {
  const url = "/source-files/types";
  yield requestSaga(GET_SOURCE_FILE_TYPES, url, "GET", null, null, null, true);
}

export const DELETE_SOURCE_FILES = generateDefaultActions("DELETE_SOURCE_FILES");
export const deleteSourceFiles = payload => makePayload(DELETE_SOURCE_FILES.value, payload);
function* deleteSourceFilesSaga({ dealId, sourceFileIds }) {
  const url = `/source-files/deal/${dealId}/file-versions`;
  yield requestSaga(DELETE_SOURCE_FILES, url, "DELETE", sourceFileIds, null, null, true);
}

export const SET_SOURCE_FILE_MANAGEMENT_SELECTIONS = generateDefaultActions("SET_SOURCE_FILE_MANAGEMENT_SELECTIONS");
export const setSourceFileManagementSelections = payload => makeWritePayload(SET_SOURCE_FILE_MANAGEMENT_SELECTIONS.value, payload);

export const CLEAR_SOURCE_FILE_MANAGEMENT_SELECTIONS = generateDefaultActions("CLEAR_SOURCE_FILE_MANAGEMENT_SELECTIONS");
export const clearSourceFileManagementSelections = () => makeWritePayload(CLEAR_SOURCE_FILE_MANAGEMENT_SELECTIONS.value,
  { ...omit(initialState, ["sourceFileTypes", "largeMriFileModal"]) });

export const actionCreators = {
  clearSourceFileManagementSelections,
  getSourceFileTypes,
  getSourceFileVersions,
  setSourceFileManagementSelections,
  uploadSourceFile,
  getAllVersions,
  deleteSourceFiles
};

// REDUCER

export function lenderReportSourceFileManagementReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case GET_SOURCE_FILE_VERSIONS.failure:
      ToastError("Failed to fetch source file versions.");

      return defaultReturn({ ...state, isLoading: false }, null, "selectedSourceFileVersions");
    case GET_SOURCE_FILE_VERSIONS.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "selectedSourceFileVersions");
    case GET_SOURCE_FILE_VERSIONS.value:
      return defaultReturn({ ...state, isLoading: true });

    case GET_SOURCE_FILE_TYPES.failure:
      ToastInfo("Could not fetch source file types");

      return defaultReturn(...state);
    case GET_SOURCE_FILE_TYPES.success:
      return defaultReturn({ ...state, isLoading: false }, sortBy(payload.data, ["source_name"]), "sourceFileTypes");
    case GET_SOURCE_FILE_TYPES.value:
      return defaultReturn({ ...state, isLoading: true });

    case SET_SOURCE_FILE_MANAGEMENT_SELECTIONS.value:
      return defaultReturn({ ...state, ...payload });
    case CLEAR_SOURCE_FILE_MANAGEMENT_SELECTIONS.value:
      return defaultReturn({ ...state, ...payload });

    case UPLOAD_SOURCE_FILE.value:
      return { ...state, isUploading: true };
    case UPLOAD_SOURCE_FILE.success:
      return { ...state, isUploading: false, uploadErrorMessage: null, uploadSuccessful: true, sourceFileDetails: payload.data,
        largeMriFileModal: payload.largeMriFile || false };
    case UPLOAD_SOURCE_FILE.failure:
      return { ...state, isUploading: false, uploadErrorMessage: "Upload failed, please try again", uploadSuccessful: false };

    case GET_ALL_VERSIONS.value:
      return { ...state, isLoading: true };
    case GET_ALL_VERSIONS.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "allVersionsForDeal");
    case GET_ALL_VERSIONS.failure:
      ToastError("Failed to fetch source file versions");

      return defaultReturn({ ...state, isLoading: false }, null, "allVersionsForDeal");

    case DELETE_SOURCE_FILES.success: {
      ToastSuccess("Source files deleted successfully");
      return defaultReturn({ ...state });
    }
    case DELETE_SOURCE_FILES.failure:
      ToastError("Failed to delete source files");

      return defaultReturn({ ...state });
    case DELETE_SOURCE_FILES.value:
      return defaultReturn({ ...state });

    default:
      return state;
  }
}

// SAGA
export function* lenderReportSourceFileManagementSaga() {
  yield takeLatest(GET_SOURCE_FILE_TYPES.value, getSourceFileTypesSaga);
  yield takeLatest(GET_SOURCE_FILE_VERSIONS.value, getSourceFileVersionsSaga);
  yield takeLatest(UPLOAD_SOURCE_FILE.value, uploadSourceFileSaga);
  yield takeLatest(GET_ALL_VERSIONS.value, getAllVersionsSaga);
  yield takeLatest(DELETE_SOURCE_FILES.value, deleteSourceFilesSaga);
}
